export const MARKETPLACE_SUBTAB_CAPS = {
  types: 'admin_panel_marketplace_types',
  categories: 'admin_panel_marketplace_categories',
  products: 'admin_panel_marketplace_products',
} as const;

export interface MarketplaceMeta {
  permissions: {
    types: boolean;
    categories: boolean;
    products: boolean;
  };
  workspace_keys: Array<{ value: string; label: string }>;
  service_types: Array<{ value: string; label: string }>;
  blob: {
    configured: boolean;
    container: string | null;
  };
}

export interface CategoryTypeRow {
  id: number;
  name: string;
  description: string | null;
  color: string | null;
  display_order: number;
  is_active: boolean;
}

export interface CategoryTypeFormValue {
  name: string;
  description: string;
  color: string;
  display_order: string;
  is_active: boolean;
}

export interface ProductCategoryRow {
  id: number;
  name: string;
  category_type: number;
  category_type_name?: string;
  workspace_key: string;
  short_description: string | null;
  long_description: string | null;
  image: string | null;
  image_url?: string | null;
  product_count?: number;
  created_at?: string;
}

export interface ProductCategoryFormValue {
  name: string;
  category_type: number | '';
  workspace_key: string;
  short_description: string;
  long_description: string;
  image: string;
  image_url: string;
}

export interface CoreOutputItem {
  name: string;
  text: string;
  path: string;
  image_url?: string;
}

export interface ProductRow {
  id: number;
  product_category: number;
  product_category_name?: string;
  name: string;
  short_description: string | null;
  long_description: string | null;
  price?: string;
  currency?: string;
  credits: number;
  core_outputs_json: CoreOutputItem[] | string;
  duration_in_days: number;
  service_type: string;
  display_order: number;
  has_ai?: boolean;
  is_free?: boolean;
  created_at?: string;
}

export interface ProductFormValue {
  product_category: number | '';
  name: string;
  short_description: string;
  long_description: string;
  service_type: string;
  display_order: string;
  credits: string;
  duration_in_days: string;
  core_outputs: CoreOutputItem[];
}

export interface BlobItem {
  name: string;
  size: number | null;
  content_type: string | null;
  last_modified: string | null;
  url: string;
}

export interface BlobListResponse {
  configured: boolean;
  container: string | null;
  blobs: BlobItem[];
  detail?: string;
}

export const emptyCategoryTypeForm = (): CategoryTypeFormValue => ({
  name: '',
  description: '',
  color: '#C3D74F',
  display_order: '0',
  is_active: true,
});

export function formFromCategoryType(row: CategoryTypeRow): CategoryTypeFormValue {
  return {
    name: row.name || '',
    description: row.description || '',
    color: row.color || '#C3D74F',
    display_order: String(row.display_order ?? 0),
    is_active: row.is_active,
  };
}

export function categoryTypeToPayload(value: CategoryTypeFormValue) {
  return {
    name: value.name.trim(),
    description: value.description.trim() || null,
    color: value.color.trim() || null,
    display_order: Number(value.display_order) || 0,
    is_active: value.is_active,
  };
}

export const emptyProductCategoryForm = (
  categoryType: number | '' = ''
): ProductCategoryFormValue => ({
  name: '',
  category_type: categoryType,
  workspace_key: '',
  short_description: '',
  long_description: '',
  image: '',
  image_url: '',
});

export function formFromProductCategory(row: ProductCategoryRow): ProductCategoryFormValue {
  return {
    name: row.name || '',
    category_type: row.category_type,
    workspace_key: row.workspace_key || '',
    short_description: row.short_description || '',
    long_description: row.long_description || '',
    image: row.image || '',
    image_url: row.image_url || '',
  };
}

export function productCategoryToPayload(value: ProductCategoryFormValue) {
  return {
    name: value.name.trim(),
    category_type: value.category_type === '' ? null : Number(value.category_type),
    workspace_key: value.workspace_key || '',
    short_description: value.short_description.trim() || null,
    long_description: value.long_description.trim() || null,
    image: value.image.trim() || null,
  };
}

function parseCoreOutputs(value: ProductRow['core_outputs_json']): CoreOutputItem[] {
  let raw: unknown = value;
  if (typeof value === 'string') {
    try {
      raw = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    const row = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    return {
      name: String(row.name ?? ''),
      text: String(row.text ?? ''),
      path: String(row.path ?? ''),
      image_url: typeof row.image_url === 'string' ? row.image_url : undefined,
    };
  });
}

export const emptyProductForm = (
  productCategory: number | '' = '',
  serviceType = 'Catalyx'
): ProductFormValue => ({
  product_category: productCategory,
  name: '',
  short_description: '',
  long_description: '',
  service_type: serviceType,
  display_order: '0',
  credits: '0',
  duration_in_days: '1',
  core_outputs: [],
});

export function formFromProduct(row: ProductRow): ProductFormValue {
  return {
    product_category: row.product_category,
    name: row.name || '',
    short_description: row.short_description || '',
    long_description: row.long_description || '',
    service_type: row.service_type || 'Catalyx',
    display_order: String(row.display_order ?? 0),
    credits: String(row.credits ?? 0),
    duration_in_days: String(row.duration_in_days ?? 1),
    core_outputs: parseCoreOutputs(row.core_outputs_json),
  };
}

export function productToPayload(value: ProductFormValue) {
  return {
    product_category: value.product_category === '' ? null : Number(value.product_category),
    name: value.name.trim(),
    short_description: value.short_description.trim() || null,
    long_description: value.long_description.trim() || null,
    service_type: value.service_type,
    display_order: Number(value.display_order) || 0,
    credits: Number(value.credits) || 0,
    duration_in_days: Math.max(1, Number(value.duration_in_days) || 1),
    core_outputs_json: value.core_outputs.map((item) => ({
      name: item.name.trim(),
      text: item.text.trim(),
      path: item.path.trim(),
    })),
  };
}
